import React, {useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import logo from "../assets/logo100.png";
import {BurgerMenu} from "./BurgerMenu";
import {useSelector, useDispatch} from "react-redux";
import {AppDispatch, RootState} from "../store/store";
import {removeFromWatchlist, clearWatchlist} from "../store/watchlistSlice";
import {logout} from "../store/authSlice";
import {clearFavorites} from "../store/favoritesSlice";
import {clearWatched} from "../store/watchedSlice";
import {useClerk} from "@clerk/clerk-react";
import {DropDown} from "./DropDown";
import {SearchInput} from "./SearchInput";
import {SettingsModal} from "./SettingsModal";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import {TOAST_CONFIG} from "../helpers/toastConfig";
import {useKeyboardShortcuts} from "../hooks/useKeyboardShortcuts";
import {ROUTES} from "../constants/routes";

export const ProfileMenu: React.FC = () => {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const {signOut} = useClerk();

  const handleLogout = async () => {
    await signOut();
    dispatch(logout());
    dispatch(clearFavorites());
    dispatch(clearWatchlist());
    dispatch(clearWatched());
    toast.success(t("logout"), TOAST_CONFIG);
    navigate(ROUTES.login);
  };

  return (
    <div className="relative flex-shrink-0">
      {isAuthenticated ? (
        <div className="relative">
          <button
            className="h-9 px-[18px] rounded-sm border border-accent bg-accent text-white text-[13px] font-semibold tracking-[0.3px] transition-all duration-fast hover:opacity-90 hover:scale-[1.03] active:scale-[0.97]"
            onClick={() => setIsDropdownOpen((v) => !v)}
          >
            {t("profile")}
          </button>
          {isDropdownOpen && (
            <div className="absolute top-[calc(100%+8px)] right-0 min-w-[180px] bg-raised border border-border2 rounded-md py-1 shadow-lg z-[300]">
              <button
                className="block w-full px-4 py-2.5 rounded-none border-none bg-transparent text-t2 text-[13px] font-medium text-left transition-all duration-fast hover:bg-raised2 hover:text-t1 cursor-pointer"
                onClick={() => { navigate(ROUTES.profile); setIsDropdownOpen(false); }}
              >
                {t("view_profile")}
              </button>
              <button
                className="block w-full px-4 py-2.5 rounded-none border-none bg-transparent text-t2 text-[13px] font-medium text-left transition-all duration-fast hover:bg-raised2 hover:text-t1 cursor-pointer"
                onClick={() => { navigate(ROUTES.settings); setIsDropdownOpen(false); }}
              >
                {t("settings")}
              </button>
              <button
                className="block w-full px-4 py-2.5 rounded-none border-none bg-transparent text-t2 text-[13px] font-medium text-left transition-all duration-fast hover:bg-raised2 hover:text-t1 cursor-pointer"
                onClick={() => { navigate(ROUTES.about); setIsDropdownOpen(false); }}
              >
                {t("about")}
              </button>
              <div className="my-1 border-t border-border" />
              <button
                className="block w-full px-4 py-2.5 rounded-none border-none bg-transparent text-accent text-[13px] font-medium text-left transition-all duration-fast hover:bg-raised2 cursor-pointer"
                onClick={handleLogout}
              >
                {t("logout")}
              </button>
            </div>
          )}
        </div>
      ) : (
        <button
          className="h-9 px-[18px] rounded-sm border border-accent bg-transparent text-t1 text-[13px] font-semibold tracking-[0.3px] transition-all duration-fast hover:bg-accent hover:text-white"
          onClick={() => navigate(ROUTES.login)}
        >
          {t("login")}
        </button>
      )}
    </div>
  );
};

export const Header: React.FC = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [isWatchlistOpen, setIsWatchlistOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const watchlist = useSelector((state: RootState) => state.watchlist.items);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const userId = useSelector((state: RootState) => state.auth.user?.id ?? null);

  useKeyboardShortcuts({
    onSearch: () => {
      // Focus the search input — try both desktop and mobile refs
      const input = document.querySelector<HTMLInputElement>('input[placeholder*="Search"], input[type="search"]');
      input?.focus();
    },
    onEscape: () => {
      setIsWatchlistOpen(false);
      setShowSettings(false);
      setShowPopup(false);
      (document.activeElement as HTMLElement)?.blur();
    },
  });

  const handleRemoveFromWatchlist = (id: number, type: "movie" | "tv") => {
    if (!userId) return;
    dispatch(removeFromWatchlist(id));
  };

  const handleWatchlistToggle = () => {
    if (!isAuthenticated) {
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }
    setIsWatchlistOpen((v) => !v);
  };

  return (
    <>
      {/* ── Desktop header (single row, 3 sections) ── */}
      <header className="sticky top-0 z-[200] backdrop-blur-xl bg-bg/[0.85] border-b border-border">
        <div className="flex items-center gap-4 px-8 h-16 max-w-[1920px] mx-auto max-[1024px]:px-5 max-[768px]:hidden">

          {/* LEFT — logo + nav */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <Link className="flex items-center gap-2 no-underline mr-3" to={ROUTES.home}>
              <img src={logo} className="h-8 w-auto" alt="Movflix" />
              <span className="text-[15px] font-bold text-t1 tracking-[-0.3px]">Movflix</span>
            </Link>
            <DropDown
              title={t("movies")}
              links={[
                {label: t("popular"), category: "popular", content_type: "movie"},
                {label: t("upcoming"), category: "upcoming", content_type: "movie"},
                {label: t("best"), category: "best", content_type: "movie"},
              ]}
            />
            <DropDown
              title={t("shows")}
              links={[
                {label: t("trending"), category: "trending", content_type: "tv"},
                {label: t("on_air_now"), category: "on_the_air", content_type: "tv"},
                {label: t("best"), category: "best", content_type: "tv"},
              ]}
            />
            <Link
              to={ROUTES.swipe}
              className="flex items-center gap-1.5 h-9 px-3.5 text-[13px] font-medium text-t2 no-underline rounded-sm transition-all duration-fast hover:text-t1 hover:bg-raised2"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
              {t("discover")}
            </Link>
            <Link
              to={ROUTES.recommendations()}
              className="flex items-center gap-1.5 h-9 px-3.5 text-[13px] font-medium text-t2 no-underline rounded-sm transition-all duration-fast hover:text-t1 hover:bg-raised2"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              {t("recommendations")}
            </Link>
            <Link
              to={ROUTES.anime()}
              className="flex items-center gap-1.5 h-9 px-3.5 text-[13px] font-medium text-t2 no-underline rounded-sm transition-all duration-fast hover:text-t1 hover:bg-raised2"
            >
              🎌 {t("anime")}
            </Link>
          </div>

          {/* CENTER — search */}
          <div className="flex-1 flex justify-center px-6">
            <div className="w-full max-w-[480px]">
              <SearchInput />
            </div>
          </div>

          {/* RIGHT — watchlist + settings + profile */}
          <div className="flex items-center gap-2 flex-shrink-0">

            {/* Watchlist */}
            <div className="relative">
              <button
                className="flex items-center gap-1.5 h-9 px-3.5 rounded-sm border border-border bg-raised text-t2 text-[13px] font-medium transition-all duration-fast hover:border-border2 hover:text-t1 hover:bg-raised2"
                onClick={handleWatchlistToggle}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                </svg>
                {t("watchlist")}
                {isAuthenticated && watchlist.length > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-[9px] bg-accent text-white text-[10px] font-bold leading-none">
                    {watchlist.length}
                  </span>
                )}
              </button>

              {isAuthenticated && isWatchlistOpen && (
                <div className="absolute top-[calc(100%+8px)] right-0 w-[300px] max-h-[360px] overflow-y-auto bg-raised border border-border2 rounded-md py-1 z-[300] shadow-lg">
                  {watchlist.length > 0 ? (
                    watchlist.map((item) => (
                      <div key={item.id} className="flex items-center gap-3 px-3 py-2 transition-colors duration-fast hover:bg-raised2">
                        <Link to={ROUTES.content(item.type, item.id, item.title ?? "")} onClick={() => setIsWatchlistOpen(false)} className="flex-shrink-0">
                          <img
                            src={`https://image.tmdb.org/t/p/w92${item.poster_path}`}
                            alt={item.title}
                            className="w-8 h-12 object-cover rounded-xs"
                          />
                        </Link>
                        <Link to={ROUTES.content(item.type, item.id, item.title ?? "")} className="flex-1 min-w-0 no-underline" onClick={() => setIsWatchlistOpen(false)}>
                          <p className="text-[13px] font-medium text-t1 line-clamp-2 hover:text-accent transition-colors duration-fast">{item.title}</p>
                        </Link>
                        <button
                          className="flex-shrink-0 h-7 px-2 rounded-xs border border-border bg-transparent text-t3 text-[11px] transition-all duration-fast hover:border-accent hover:text-accent hover:bg-accent-muted cursor-pointer"
                          onClick={() => handleRemoveFromWatchlist(item.id, item.type)}
                        >
                          ✕
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="py-6 text-center text-[13px] text-t3">{t("empty_watchlist")}</p>
                  )}
                </div>
              )}
            </div>

            {/* Settings */}
            <button
              className="flex items-center justify-center w-9 h-9 rounded-sm border border-border bg-raised text-t2 transition-all duration-fast hover:border-border2 hover:text-t1 hover:bg-raised2"
              onClick={() => setShowSettings(true)}
              aria-label="Settings"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </button>

            <ProfileMenu />
          </div>
        </div>

        {/* ── Mobile header (2 rows) ── */}
        <div className="hidden max-[768px]:flex flex-col px-4 py-3 gap-2.5">
          {/* Row 1: logo + actions */}
          <div className="flex items-center justify-between">
            <Link className="flex items-center gap-2 no-underline" to={ROUTES.home}>
              <img src={logo} className="h-8 w-auto" alt="Movflix" />
              <span className="text-[15px] font-bold text-t1">Movflix</span>
            </Link>
            <div className="flex items-center gap-1.5">
              <button
                className="flex items-center justify-center w-9 h-9 rounded-sm border border-border bg-raised text-t2 transition-all duration-fast hover:border-border2 hover:text-t1"
                onClick={() => setShowSettings(true)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
              <ProfileMenu />
            </div>
          </div>
          {/* Row 2: search full width */}
          <SearchInput />
          {/* Row 3: nav links */}
          <div className="flex items-center gap-1">
            <DropDown
              title={t("movies")}
              links={[
                {label: t("popular"), category: "popular", content_type: "movie"},
                {label: t("upcoming"), category: "upcoming", content_type: "movie"},
                {label: t("best"), category: "best", content_type: "movie"},
              ]}
            />
            <DropDown
              title={t("shows")}
              links={[
                {label: t("trending"), category: "trending", content_type: "tv"},
                {label: t("on_air_now"), category: "on_the_air", content_type: "tv"},
                {label: t("best"), category: "best", content_type: "tv"},
              ]}
            />
            <Link
              to={ROUTES.recommendations()}
              className="flex items-center gap-1.5 h-9 px-3 text-[13px] font-medium text-t2 no-underline rounded-sm transition-all duration-fast hover:text-t1 hover:bg-raised2"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              {t("recommendations")}
            </Link>
            <Link
              to={ROUTES.anime()}
              className="flex items-center gap-1 h-9 px-3 text-[13px] font-medium text-t2 no-underline rounded-sm transition-all duration-fast hover:text-t1 hover:bg-raised2"
            >
              🎌 {t("anime")}
            </Link>
          </div>
        </div>
      </header>

      <BurgerMenu isOpen={false} />

      {/* Login required popup */}
      {showPopup && (
        <div className="fixed bottom-6 right-6 bg-raised border border-accent text-t1 py-3 px-5 rounded-md shadow-accent z-[1000]">
          <p className="text-[13px] font-medium text-t1">{t("please_log_in")}</p>
        </div>
      )}

      {/* Settings modal */}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    </>
  );
};
